import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import FaceAlignPreview from './FaceAlignPreview'
import {
  FACE_CAPTURE_CONFIG,
  FACE_RESULT_ALWAYS_OK,
  FACE_SESSION_STATUS,
  awaitFaceResult,
  cancelFaceSession,
  startFaceSession,
  subscribeFaceSession
} from '@/views/surveillance/face/faceCaptureSession'
import './index.less'

/** 去向盘：需刷脸槽位仅在识别成功且已有抓拍图后可点 */
export function isFaceReadyForDestination (faceSnapshot) {
  if (FACE_RESULT_ALWAYS_OK) return true
  if (faceSnapshot?.status !== FACE_SESSION_STATUS.READY) return false
  const result = faceSnapshot?.result
  if (!result) return false
  if (!result.nurseId || !result.nurseName) return false
  if (typeof Blob === 'undefined' || !(result.nurseAvatar instanceof Blob)) return false
  return true
}

/**
 * 本机刷脸：订阅会话、开流、重拍、取景。
 * 开流必须在用户点击里调用 ref.start()，不能放到 effect。
 *
 * ref：start() / stop() / awaitResult()
 * onChange：会话快照，供按钮禁用和文案
 */
const FaceCapture = forwardRef(function FaceCapture ({
  sessionKey,
  active = false,
  onChange
}, ref) {
  const [snapshot, setSnapshot] = useState(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const publish = useCallback((next) => {
    setSnapshot(next)
    if (typeof onChangeRef.current === 'function') {
      onChangeRef.current(next)
    }
  }, [])

  useEffect(() => {
    if (!active || !sessionKey) {
      publish(null)
      return undefined
    }
    const unsubscribe = subscribeFaceSession(sessionKey, publish)
    return () => {
      unsubscribe()
    }
  }, [active, sessionKey, publish])

  useEffect(() => () => {
    if (sessionKey) cancelFaceSession(sessionKey)
  }, [sessionKey])

  const recapture = useCallback(() => {
    if (!sessionKey) return
    publish({
      status: FACE_SESSION_STATUS.RECOGNIZING,
      reason: null,
      result: null,
      streamActive: false
    })
    startFaceSession(sessionKey)
  }, [publish, sessionKey])

  useImperativeHandle(ref, () => ({
    start () {
      if (!sessionKey) return
      startFaceSession(sessionKey)
    },
    stop () {
      if (!sessionKey) return
      cancelFaceSession(sessionKey)
    },
    awaitResult (options = {}) {
      return awaitFaceResult(sessionKey, {
        timeoutMs: FACE_CAPTURE_CONFIG.awaitMergeMs,
        ...options
      })
    }
  }), [sessionKey])

  if (!active || !sessionKey) return null

  return (
    <FaceAlignPreview
      cellKey={sessionKey}
      faceSnapshot={snapshot}
      active={active}
      onRecapture={recapture}
    />
  )
})

export default FaceCapture
